import type { Book } from "@/data/books";

export interface Mood {
  id: string;
  label: string;
  emoji: string;
  gradient: string; // tailwind gradient classes
  match: (b: Book) => boolean;
}

const has = (b: Book, re: RegExp) =>
  (b.tropes ?? []).some((t) => re.test(t.toLowerCase())) ||
  re.test((b.hook ?? "").toLowerCase());

// TODO: admin-managed moods later. Hard-coded for MVP.
export const MOODS: Mood[] = [
  {
    id: "devastating",
    label: "Emotionally Devastating",
    emoji: "🥀",
    gradient: "from-rose-700 to-red-900",
    match: (b) => b.emotionalIntensity === "devastating",
  },
  {
    id: "possessive",
    label: "Possessive MMC",
    emoji: "🔥",
    gradient: "from-orange-600 to-red-700",
    match: (b) => has(b, /possessive|obsess|alpha|dominant/),
  },
  {
    id: "cozy",
    label: "Cozy Comfort Romance",
    emoji: "🕯️",
    gradient: "from-amber-400 to-rose-400",
    match: (b) => b.emotionalIntensity === "light" || has(b, /comfort|cozy|small town/),
  },
  {
    id: "toxic",
    label: "Toxic Obsession",
    emoji: "💀",
    gradient: "from-purple-800 to-rose-900",
    match: (b) => has(b, /obsess|toxic|stalker|dark/) && b.spiceLevel >= 2,
  },
  {
    id: "touch-her",
    label: "Touch-Her-and-Die Energy",
    emoji: "🗡️",
    gradient: "from-red-700 to-slate-900",
    match: (b) => has(b, /possessive|protective|mafia|bodyguard/),
  },
  {
    id: "slow-burn",
    label: "Slow Burn Longing",
    emoji: "⏳",
    gradient: "from-pink-500 to-rose-700",
    match: (b) => has(b, /slow burn|longing|pining/),
  },
  {
    id: "forbidden",
    label: "Forbidden Attraction",
    emoji: "🗝️",
    gradient: "from-fuchsia-700 to-purple-900",
    match: (b) => has(b, /forbidden|enemies|secret|rivals|taboo/),
  },
  {
    id: "healing",
    label: "Healing After Chaos",
    emoji: "🌿",
    gradient: "from-emerald-500 to-rose-500",
    match: (b) => has(b, /healing|hurt\/comfort|grovel|second chance/),
  },
  {
    id: "kick-feet",
    label: "Kick-Your-Feet Romance",
    emoji: "🦋",
    gradient: "from-pink-400 to-purple-400",
    match: (b) => b.emotionalIntensity === "light" && b.spiceLevel <= 2,
  },
  {
    id: "morally-gray",
    label: "Morally Gray Men",
    emoji: "🖤",
    gradient: "from-zinc-700 to-rose-800",
    match: (b) => has(b, /morally gray|anti-hero|villain|mafia|alpha/),
  },
  {
    id: "ruin",
    label: "Emotional Ruin",
    emoji: "💔",
    gradient: "from-red-800 to-purple-950",
    match: (b) => b.emotionalIntensity === "devastating" && has(b, /angst|tragedy|grief/),
  },
  {
    id: "high-tension",
    label: "High Tension / Angst",
    emoji: "⚡",
    gradient: "from-yellow-500 to-red-700",
    match: (b) => has(b, /angst|tension|enemies|forbidden/),
  },
  {
    id: "soft-safe",
    label: "Soft & Safe Romance",
    emoji: "🌸",
    gradient: "from-rose-300 to-pink-500",
    match: (b) => b.emotionalIntensity === "light" && b.spiceLevel <= 1,
  },
];

export const moodById = (id: string | null | undefined) =>
  id ? MOODS.find((m) => m.id === id) ?? null : null;

const MOOD_KEY = "rr.moodTonight";
export const loadMood = (): string | null => {
  try { return localStorage.getItem(MOOD_KEY); } catch { return null; }
};
export const saveMood = (id: string | null) => {
  try {
    if (id) localStorage.setItem(MOOD_KEY, id);
    else localStorage.removeItem(MOOD_KEY);
  } catch { /* ignore */ }
};
